import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, styled } from '@mui/material';
import { managerIntelAPI, playersAPI } from '../../utils/api';

const Wrapper = styled(Box)({
  marginTop: '32px',
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: '16px',
  gap: '16px',
});

const HeaderTitle = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  color: '#86868B',
});

const HeaderCount = styled(Typography)({
  fontSize: '13px',
  color: '#6E6E73',
});

const TradeList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const TradeRow = styled(Box)(({ highlight }) => ({
  position: 'relative',
  background: '#FFFFFF',
  border: highlight ? '1px solid #1D1D1F' : '1px solid #EBEBED',
  borderRadius: '16px',
  padding: '20px 22px',
  transition: 'all 0.2s ease',
}));

const TradeMeta = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
  paddingBottom: '14px',
  borderBottom: '1px solid #F2F2F5',
  gap: '12px',
  flexWrap: 'wrap',
});

const TradeMetaLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: 0,
  flexWrap: 'wrap',
});

const TradeWhen = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1D1D1F',
  letterSpacing: '-0.01em',
});

const TradeContext = styled(Box)({
  fontSize: '13px',
  color: '#86868B',
  letterSpacing: '-0.01em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
});

const HighlightBadge = styled(Typography)({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  color: '#FFFFFF',
  background: '#1D1D1F',
  borderRadius: '980px',
  padding: '4px 10px',
  whiteSpace: 'nowrap',
});

const TradeBody = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: '20px',
  alignItems: 'stretch',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
});

const SideBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 0,
});

const SideLabel = styled(Typography)({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '1.4px',
  textTransform: 'uppercase',
  color: '#86868B',
});

const PlayerChip = styled(Box)(({ highlight }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 12px 8px 8px',
  borderRadius: '10px',
  background: highlight ? '#1D1D1F' : '#F5F5F7',
  color: highlight ? '#FFFFFF' : '#1D1D1F',
  minWidth: 0,
}));

const PlayerImg = styled(Box)(({ highlight }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  overflow: 'hidden',
  background: highlight ? 'rgba(255,255,255,0.18)' : '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  color: highlight ? '#FFFFFF' : '#6E6E73',
  flex: '0 0 32px',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center',
  },
}));

const PickBadge = styled(Box)({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  padding: '3px 6px',
  borderRadius: '5px',
  background: '#FFFFFF',
  color: '#6E6E73',
  flex: '0 0 auto',
});

const LeagueAvatar = styled(Box)({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  overflow: 'hidden',
  background: '#EBEBED',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '9px',
  fontWeight: 700,
  color: '#86868B',
  flex: '0 0 18px',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const PlayerName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const PickChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  borderRadius: '10px',
  background: '#FAFAFA',
  border: '1px dashed #D2D2D7',
  fontSize: '13px',
  fontWeight: 500,
  color: '#6E6E73',
  letterSpacing: '-0.01em',
});

const Arrow = styled(Box)({
  color: '#C7C7CC',
  fontSize: '20px',
  fontWeight: 400,
  alignSelf: 'center',
  paddingTop: '12px',
  '@media (max-width: 600px)': { display: 'none' },
});

const SideEmpty = styled(Typography)({
  fontSize: '13px',
  color: '#C7C7CC',
  fontStyle: 'italic',
  padding: '10px 12px',
});

const Empty = styled(Typography)({
  textAlign: 'center',
  color: '#86868B',
  fontSize: '14px',
  padding: '24px 0',
});

const FilterRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginBottom: '12px',
  flexWrap: 'wrap',
});

const FilterChip = styled(Box)(({ active }) => ({
  padding: '6px 14px',
  borderRadius: '980px',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.2s ease',
  background: active ? '#000000' : '#F5F5F7',
  color: active ? '#FFFFFF' : '#1D1D1F',
  '&:hover': {
    background: active ? '#000000' : '#EBEBED',
  },
}));

// Player photo with graceful fallback. Always constructs Sleeper's CDN URL from the
// sleeper_id (DB image_url is unreliable) and swaps to position text on 404.
const SmartPlayerImg = ({ sleeperId, fallback, highlight }) => {
  const [errored, setErrored] = useState(false);
  const url = sleeperId
    ? `https://sleepercdn.com/content/nfl/players/thumb/${sleeperId}.jpg`
    : null;
  if (!url || errored) return <PlayerImg highlight={highlight}>{fallback}</PlayerImg>;
  return (
    <PlayerImg highlight={highlight}>
      <img src={url} alt="" onError={() => setErrored(true)} />
    </PlayerImg>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days < 1) return 'Today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPick = (pick) => {
  const round = pick.round;
  const suffix = round === 1 ? 'st' : round === 2 ? 'nd' : round === 3 ? 'rd' : 'th';
  return `${pick.season} ${round}${suffix}`;
};

const PartnerTradesInline = ({
  partnerSleeperUserId,
  partnerDisplayName,
  viewerSleeperUsername,
  viewerRosterPlayerIds = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [playerData, setPlayerData] = useState({});
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'mine'

  useEffect(() => {
    if (!partnerSleeperUserId || !viewerSleeperUsername) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await managerIntelAPI.getPartnerTrades(
          partnerSleeperUserId,
          viewerSleeperUsername
        );
        if (cancelled) return;
        const payload = res.data || {};
        setData(payload);

        const ids = new Set();
        (payload.trades || []).forEach((trade) => {
          Object.keys(trade.partnerAdds || {}).forEach((pid) => ids.add(pid));
          Object.keys(trade.partnerDrops || {}).forEach((pid) => ids.add(pid));
        });
        if (ids.size > 0) {
          const pr = await playersAPI.getBatch(Array.from(ids));
          if (!cancelled) setPlayerData(pr.data.players || {});
        }
      } catch (err) {
        if (!cancelled) setError('Could not load trades.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partnerSleeperUserId, viewerSleeperUsername]);

  const myRosterSet = useMemo(
    () => new Set((viewerRosterPlayerIds || []).map(String)),
    [viewerRosterPlayerIds]
  );

  const trades = data?.trades || [];

  const playerName = (id) => playerData[id]?.name || `Player ${id}`;
  const playerPos = (id) => playerData[id]?.position || '—';

  const renderPlayer = (id, isMine) => (
    <PlayerChip key={id} highlight={isMine}>
      <SmartPlayerImg sleeperId={id} fallback={playerPos(id)} highlight={isMine} />
      <PlayerName>{playerName(id)}</PlayerName>
    </PlayerChip>
  );

  const renderPick = (pick, key) => (
    <PickChip key={key}>
      <PickBadge>PICK</PickBadge>
      <Box sx={{ fontWeight: 500, color: '#1D1D1F' }}>{formatPick(pick)}</Box>
    </PickChip>
  );

  const renderTrade = (trade) => {
    const adds = Object.keys(trade.partnerAdds || {});
    const drops = Object.keys(trade.partnerDrops || {});
    const allIds = [...adds, ...drops];
    const involvesMyPlayer = allIds.some((id) => myRosterSet.has(String(id)));

    const picksGot = (trade.draftPicksTraded || []).filter(
      (p) => p.toRosterId === trade.partnerRosterId
    );
    const picksGave = (trade.draftPicksTraded || []).filter(
      (p) => p.fromRosterId === trade.partnerRosterId
    );

    const counterpartyName = trade.counterparties?.[0]?.displayName;
    const leagueName = trade.league?.name;
    const leagueAvatarUrl = trade.league?.avatarUrl;

    return (
      <TradeRow key={trade.id} highlight={involvesMyPlayer}>
        <TradeMeta>
          <TradeMetaLeft>
            <TradeWhen>{formatDate(trade.tradedAt)}</TradeWhen>
            <TradeContext>
              {leagueName && (
                <>
                  <LeagueAvatar>
                    {leagueAvatarUrl ? (
                      <img
                        src={`https://sleepercdn.com/avatars/thumbs/${leagueAvatarUrl}`}
                        alt=""
                      />
                    ) : (
                      (leagueName[0] || '').toUpperCase()
                    )}
                  </LeagueAvatar>
                  <span>{leagueName}</span>
                </>
              )}
              {counterpartyName && (
                <span>{leagueName ? '· ' : ''}with {counterpartyName}</span>
              )}
            </TradeContext>
          </TradeMetaLeft>
          {involvesMyPlayer && <HighlightBadge>Your player</HighlightBadge>}
        </TradeMeta>

        <TradeBody>
          <SideBlock>
            <SideLabel>Received</SideLabel>
            {adds.length === 0 && picksGot.length === 0 && (
              <SideEmpty>—</SideEmpty>
            )}
            {adds.map((id) => renderPlayer(id, myRosterSet.has(String(id))))}
            {picksGot.map((p, i) => renderPick(p, `pg-${i}`))}
          </SideBlock>

          <Arrow>→</Arrow>

          <SideBlock>
            <SideLabel>Sent</SideLabel>
            {drops.length === 0 && picksGave.length === 0 && (
              <SideEmpty>—</SideEmpty>
            )}
            {drops.map((id) => renderPlayer(id, myRosterSet.has(String(id))))}
            {picksGave.map((p, i) => renderPick(p, `pgv-${i}`))}
          </SideBlock>
        </TradeBody>
      </TradeRow>
    );
  };

  const tradeTouchesMyRoster = (t) => {
    const ids = [
      ...Object.keys(t.partnerAdds || {}),
      ...Object.keys(t.partnerDrops || {}),
    ];
    return ids.some((id) => myRosterSet.has(String(id)));
  };

  const highlightedCount = useMemo(
    () => trades.filter(tradeTouchesMyRoster).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trades, myRosterSet]
  );

  const visibleTrades = useMemo(() => {
    if (filter === 'mine') return trades.filter(tradeTouchesMyRoster);
    return trades;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades, filter, myRosterSet]);

  return (
    <Wrapper>
      <Header>
        <HeaderTitle>{partnerDisplayName}'s trade history</HeaderTitle>
        {trades.length > 0 && (
          <HeaderCount>
            {trades.length} {trades.length === 1 ? 'trade' : 'trades'}
            {highlightedCount > 0
              ? ` · ${highlightedCount} involve your players`
              : ''}
          </HeaderCount>
        )}
      </Header>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <CircularProgress size={20} sx={{ color: '#86868B' }} />
        </Box>
      )}
      {error && <Empty>{error}</Empty>}
      {!loading && !error && trades.length === 0 && (
        <Empty>No trade history available.</Empty>
      )}
      {!loading && !error && trades.length > 0 && (
        <>
          <FilterRow>
            <FilterChip
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              All trades
            </FilterChip>
            <FilterChip
              active={filter === 'mine'}
              onClick={() => setFilter('mine')}
            >
              Your players{highlightedCount > 0 ? ` · ${highlightedCount}` : ''}
            </FilterChip>
          </FilterRow>
          {visibleTrades.length === 0 ? (
            <Empty>None of these trades involve your players.</Empty>
          ) : (
            <TradeList>{visibleTrades.map(renderTrade)}</TradeList>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default PartnerTradesInline;
